import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import classNames from 'classnames';
import { Line } from "react-chartjs-2";
import styleAf from "./Affiliate.module.scss";
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AffiliateStats, AffiliateStatsTitle, ChartStat } from '../../../interfaces';
import { ErrorBoundary } from '../../common';
import { equalsIgnoreCase } from '../../../_utils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const shadowPlugin = {
  id: 'lineShadow',
  beforeDatasetsDraw(chart, args, pluginOptions) {
    const { ctx } = chart;

    chart.data.datasets.forEach((dataset, index) => {
      const meta = chart.getDatasetMeta(index);
      if (!meta || meta.type !== 'line') return;

      const points = meta.data;
      meta.dataset.draw = () => {};

      const times = pluginOptions.repeat || 5;
      const offsetStep = pluginOptions.offsetStep || 10;

      for (let i = 0; i < times; i++) {
        ctx.save();

        ctx.shadowColor = pluginOptions.shadowColor || dataset.borderColor || 'rgba(0,0,0,1)';
        ctx.shadowBlur = pluginOptions.shadowBlur || 15;
        ctx.shadowOffsetX = (pluginOptions.shadowOffsetX || 0);
        ctx.shadowOffsetY = (pluginOptions.shadowOffsetY || 4) + i * offsetStep;

        ctx.beginPath();
        points.forEach((point, j) => {
          if (j === 0) {
            ctx.moveTo(point.x, point.y);
          } else {
            ctx.lineTo(point.x, point.y);
          }
        });

        ctx.strokeStyle = dataset.borderColor;
        ctx.lineWidth = dataset.borderWidth || 2;
        ctx.stroke();

        ctx.restore();
      }
    });
  }
};

const initData = {
  labels: [],
  datasets: [
    {
      label: '',
      data: [],
      borderColor: 'rgba(249, 132, 146, 1)',
      backgroundColor: 'rgba(19, 23, 34, 1)',
      pointHoverRadius: 0,
      pointRadius: 0,
      tension: 0.5
    },
  ],
};

interface IProp {
  info: ChartStat[], 
  activeRange: string, 
  activeStat: AffiliateStats
}

export const AffiliateChart = ({ info, activeRange, activeStat }: IProp) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState(initData);

  const getYScaleOptions = useCallback(() => {
    if (!info) return {
      display: false
    };
    const isFullNumber = (equalsIgnoreCase(activeStat, AffiliateStats.registrations) 
      || equalsIgnoreCase(activeStat, AffiliateStats.linkClicks) 
      || equalsIgnoreCase(activeStat, AffiliateStats.firstTimeDepositors));

    return {
      type: 'linear' as const,
      display: true,
      position: 'right' as const,
      min: 0,
      ticks: {
        color: 'rgb(127, 127, 127)',
        maxTicksLimit: 10,
        stepSize: isFullNumber ? 1 : undefined,
        callback: (value: number) => {
          if (isFullNumber && !Number.isInteger(value)) return null;
          if (isFullNumber) return value;
          return `$${value}`;
        },
      },
    };
  }, [activeStat]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      },
      lineShadow: {
        shadowColor: 'rgba(118, 63, 229, 1)',
        shadowBlur: 25,
        shadowOffsetY: 4,
        repeat: 5,
        offsetStep: 10  
      },
    },
    scales: {
      y: getYScaleOptions(),
      x: {
        ticks: {
          color: "rgb(127, 127, 127)"
        },
      }
    }
  };

  useEffect(() => {
    if (!info) return;
    const labels = info.map(e => new Date(e.time).toLocaleDateString());
    const revenues = info.map(e => e.value);

    const data = {
      labels,
      datasets: [
        {
          ...initData.datasets[0],
          data: revenues,
        },
      ],
    };
    setChartData(data);
  }, [info])

  return (
    <div className={classNames(styleAf.grid_item, styleAf.chart_item)}>
    <div className={styleAf.chart_header}>
      <span>{ t(AffiliateStatsTitle[activeStat]) }</span>
      <span className={styleAf.filter}>{activeRange}</span>
    </div>

    { !info?.length &&
      <span className={styleAf.empy_state}>{ t("affiliates.no_activity_yet") }</span>
    }
    <ErrorBoundary>
      <div className={styleAf.chart}>
        <Line options={options} data={chartData} plugins={[shadowPlugin]}/>
      </div>
    </ErrorBoundary>
  </div>
  )
}
